import React, { useState, useEffect } from "react";
import MessageItem from "./MessageItem";
import SocialMediadashboardSidebar from "../SocialMediadashboardSidebar";
import { useAuth } from "../../../Context/AuthContext";
import axios from 'axios';
import { FiMessageSquare, FiRefreshCw, FiHeart, FiShare2 } from 'react-icons/fi';

function Engagement() {
  const [activeTab, setActiveTab] = useState('dms');
  const [engagementData, setEngagementData] = useState({
    dms: [],
    comments: [],
    mentions: []
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const { googleUserInfo, user } = useAuth();

  // Platform icons mapping
  const platformIcons = {
    facebook: '🔵',
    instagram: '📸',
    twitter: '🐦',
    linkedin: '💼',
    youtube: '📺',
    tiktok: '🎵',
    pinterest: '📌'
  };

  // Platform names mapping
  const platformNames = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    twitter: 'Twitter',
    linkedin: 'LinkedIn',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    pinterest: 'Pinterest'
  };

  // Load social accounts
  useEffect(() => {
    const loadSocialAccounts = () => {
      try {
        const storedAccounts = JSON.parse(localStorage.getItem('socialAccounts') || '[]');
        console.log('Loaded social accounts:', storedAccounts);
        setSocialAccounts(storedAccounts);
        return storedAccounts;
      } catch (error) {
        console.error('Error loading social accounts:', error);
        return [];
      }
    };

    loadSocialAccounts();
  }, []);

  // Test Facebook API permissions and capabilities
  const testFacebookPermissions = async (pageAccessToken, pageId) => {
    try {
      console.log(`Testing permissions for page ${pageId}`);
      
      // Test basic page info
      const pageInfo = await axios.get(
        `https://graph.facebook.com/v19.0/${pageId}?fields=id,name,access_token&access_token=${pageAccessToken}`
      );
      console.log('Page info:', pageInfo.data);

      // Test posts access
      const postsResponse = await axios.get(
        `https://graph.facebook.com/v19.0/${pageId}/posts?fields=id,message,created_time&limit=3&access_token=${pageAccessToken}`
      );
      console.log('Posts response:', postsResponse.data);

      // Test if we can access insights
      try {
        const insightsResponse = await axios.get(
          `https://graph.facebook.com/v19.0/${pageId}/insights?metric=page_impressions&period=day&access_token=${pageAccessToken}`
        );
        console.log('Insights response:', insightsResponse.data);
      } catch (insightsError) {
        console.log('Insights not available:', insightsError.response?.data);
      }

      return {
        hasPostsAccess: postsResponse.data.data && postsResponse.data.data.length > 0,
        posts: postsResponse.data.data || []
      };
    } catch (error) {
      console.error('Error testing Facebook permissions:', error);
      console.error('Error details:', error.response?.data);
      return {
        hasPostsAccess: false,
        posts: []
      };
    }
  };

  // Fetch Facebook posts (this usually works with basic permissions)
  const fetchFacebookPosts = async (pageAccessToken, pageId) => {
    try {
      console.log(`Fetching Facebook posts for page ${pageId}`);
      
      const postsResponse = await axios.get(
        `https://graph.facebook.com/v19.0/${pageId}/posts?fields=id,message,created_time,likes.limit(1).summary(true),shares&limit=10&access_token=${pageAccessToken}`
      );
      
      console.log('Facebook posts response:', postsResponse.data);
      
      return postsResponse.data.data || [];
    } catch (error) {
      console.error('Error fetching Facebook posts:', error);
      console.error('Error details:', error.response?.data);
      return [];
    }
  };

  // Try to fetch comments with proper error handling
  const tryFetchComments = async (postId, accessToken) => {
    try {
      console.log(`Attempting to fetch comments for post ${postId}`);
      
      const commentsResponse = await axios.get(
        `https://graph.facebook.com/v19.0/${postId}/comments?fields=from,message,created_time&limit=5&access_token=${accessToken}`
      );
      
      console.log(`Comments for post ${postId}:`, commentsResponse.data);
      return commentsResponse.data.data || [];
    } catch (error) {
      console.error(`Cannot fetch comments for post ${postId}:`, error.response?.data || error.message);
      return [];
    }
  };

  // Generate engagement data from available Facebook data
  const generateEngagementFromFacebook = (posts, page, account) => {
    const comments = [];
    const mentions = [];

    posts.forEach(post => {
      // Create comment-like items from post engagement
      if (post.likes?.summary?.total_count > 0) {
        comments.push({
          id: `${post.id}_like_engagement`,
          platform: 'facebook',
          pageId: page.id,
          postId: post.id,
          username: 'Multiple Users',
          message: `Liked your post: "${post.message?.substring(0, 50) || 'Your post'}..."`,
          time: post.created_time,
          postPreview: post.message?.substring(0, 100) || 'Photo/Video Post',
          isMention: false,
          likes: post.likes.summary.total_count,
          type: 'likes_engagement'
        });
      }

      // Create share engagement items
      if (post.shares?.count > 0) {
        mentions.push({
          id: `${post.id}_share_engagement`,
          platform: 'facebook',
          pageId: page.id,
          username: 'Your Audience',
          message: `Shared your post ${post.shares.count} times`,
          time: post.created_time,
          source: 'Post Shares',
          isMention: true,
          engagement: post.shares.count,
          type: 'shares_engagement'
        });
      }
    });

    return { comments, mentions };
  };

  // Load engagement data
  const loadEngagementData = async () => {
    if (socialAccounts.length === 0) {
      console.log('No social accounts connected');
      setEngagementData({ dms: [], comments: [], mentions: [] });
      return;
    }

    setLoading(true);
    setRefreshing(true);

    try {
      const allDms = [];
      const allComments = [];
      const allMentions = [];

      console.log('Starting to load engagement data from:', socialAccounts.length, 'accounts');

      // Process each connected account
      for (const account of socialAccounts) {
        console.log('Processing account:', account.platform, account.username);

        if (account.platform === 'facebook' && account.access_token) {
          // Process each page in the account
          for (const page of account.pages || []) {
            try {
              console.log('Processing Facebook page:', page.name, page.id);

              // Test what permissions we have
              const permissions = await testFacebookPermissions(page.access_token, page.id);
              
              // Fetch posts (this usually works)
              const posts = await fetchFacebookPosts(page.access_token, page.id);
              console.log(`Found ${posts.length} posts for page ${page.name}`);

              // Generate engagement data from posts
              const engagement = generateEngagementFromFacebook(posts, page, account);
              allComments.push(...engagement.comments);
              allMentions.push(...engagement.mentions);

              // Note: DMs require special permissions that are rarely granted
              // We'll skip DMs for now as they require pages_messaging permission

            } catch (error) {
              console.error(`Error processing Facebook page ${page.name}:`, error);
            }
          }
        } else {
          console.log(`Skipping ${account.platform} - integration not fully implemented`);
        }
      }

      console.log('Final engagement data:', {
        dms: allDms.length,
        comments: allComments.length,
        mentions: allMentions.length
      });

      setEngagementData({
        dms: allDms.sort((a, b) => new Date(b.time) - new Date(a.time)),
        comments: allComments.sort((a, b) => new Date(b.time) - new Date(a.time)),
        mentions: allMentions.sort((a, b) => new Date(b.time) - new Date(a.time))
      });

    } catch (error) {
      console.error('Error loading engagement data:', error);
      setEngagementData({ dms: [], comments: [], mentions: [] });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Refresh data
  const refreshData = async () => {
    setRefreshing(true);
    await loadEngagementData();
  };

  // Load data when component mounts or social accounts change
  useEffect(() => {
    loadEngagementData();
  }, [socialAccounts]);

  // Format time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return `${Math.floor(diffInHours * 60)}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get current data based on active tab
  const getCurrentData = () => {
    return engagementData[activeTab] || [];
  };

  return (
    <div className="flex py-4 mr-8 min-h-screen">
      <SocialMediadashboardSidebar />
      <div className="ml-4 w-full px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex">
            <button 
              className={`px-4 py-2 font-semibold rounded-lg mr-2 ${
                activeTab === 'dms' 
                  ? 'text-[#FD5F20] bg-orange-50' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('dms')}
            >
              DMs ({engagementData.dms.length})
            </button>
            <button 
              className={`px-4 py-2 font-semibold rounded-lg mr-2 ${
                activeTab === 'comments' 
                  ? 'text-[#FD5F20] bg-orange-50' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('comments')}
            >
              Comments ({engagementData.comments.length})
            </button>
            <button 
              className={`px-4 py-2 font-semibold rounded-lg ${
                activeTab === 'mentions' 
                  ? 'text-[#FD5F20] bg-orange-50' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('mentions')}
            >
              Mentions ({engagementData.mentions.length})
            </button>
          </div>

          <div className="flex items-center">
            {/* Refresh Button */}
            <button 
              onClick={refreshData}
              disabled={refreshing}
              className="mr-2 p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
            >
              <FiRefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>

            <select className="border rounded px-2 py-1 mr-2 text-sm">
              <option>All Platforms</option>
              {socialAccounts.map(account => (
                <option key={account.account_id} value={account.platform}>
                  {platformNames[account.platform]}
                </option>
              ))}
            </select>

            <button className="bg-gray-200 rounded px-2 py-1 mr-1 text-sm">Today</button>
            <button className="bg-gray-200 rounded px-2 py-1 mr-1 text-sm">Week</button>
            <button className="bg-gray-200 rounded px-2 py-1 mr-2 text-sm">Month</button>

            {/* Filter and Sort buttons */}
            <button className="mr-1 p-2 rounded-lg hover:bg-gray-100">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0M3.75 18H7.5" />
              </svg>
            </button>

            <button className="p-2 rounded-lg hover:bg-gray-100">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Title */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Engagement</h2>
          {socialAccounts.length === 0 && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-3 py-1 rounded-lg text-sm">
              📱 Connect accounts to see engagement data
            </div>
          )}
        </div>

        {/* API Limitations Info */}
        {socialAccounts.length > 0 && engagementData.comments.length === 0 && engagementData.mentions.length === 0 && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiMessageSquare className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Limited Data Available
                </h3>
                <div className="mt-1 text-sm text-blue-700">
                  <p>
                    Your Facebook app permissions don't include access to comments and private messages. 
                    To see full engagement data, you need additional permissions from Facebook.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FD5F20] mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading engagement data...</p>
          </div>
        )}

        {/* Engagement Data */}
        {!loading && (
          <div className="space-y-4">
            {getCurrentData().length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {socialAccounts.length === 0 
                  ? "Connect social media accounts to see engagement data."
                  : `No ${activeTab} found with current permissions.`
                }
              </div>
            ) : (
              getCurrentData().map((item) => (
                <div key={item.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {/* Platform Icon */}
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm">
                        {platformIcons[item.platform]}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-gray-900">{item.username}</span>
                            <span className="text-xs text-gray-500">•</span>
                            <span className="text-xs text-gray-500">
                              {platformNames[item.platform]}
                            </span>
                            {item.pageId && (
                              <>
                                <span className="text-xs text-gray-500">•</span>
                                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                  Page
                                </span>
                              </>
                            )}
                            {item.type && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                Engagement
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatTime(item.time)}
                          </span>
                        </div>

                        {/* Message Content */}
                        <p className="text-gray-700 mb-2">{item.message}</p>

                        {/* Additional Info */}
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          {item.postPreview && (
                            <span className="text-gray-400">Post: {item.postPreview}...</span>
                          )}
                          {item.source && (
                            <span className="text-gray-400">Source: {item.source}</span>
                          )}
                          {item.isMention && (
                            <span className="text-[#FD5F20] font-medium">Mention</span>
                          )}
                          {/* Engagement metrics */}
                          {item.likes && (
                            <div className="flex items-center space-x-1">
                              <FiHeart className="w-3 h-3" />
                              <span>{item.likes}</span>
                            </div>
                          )}
                          {item.engagement && (
                            <div className="flex items-center space-x-1">
                              <FiShare2 className="w-3 h-3" />
                              <span>{item.engagement} engagements</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-2 ml-4">
                      {item.type === 'likes_engagement' && (
                        <button className="text-[#FD5F20] text-sm font-medium hover:text-[#e55a1d]">
                          View Post
                        </button>
                      )}
                      {item.type === 'shares_engagement' && (
                        <button className="text-[#FD5F20] text-sm font-medium hover:text-[#e55a1d]">
                          See Shares
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Engagement;